"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Reveal from "~/componentsDS/Reveal";
import RightArrowIcon from "@/icons/arrows/arrow-next.svg"
import GraphIcon from "@/icons/products/oestra/pcos/result/arrow-graph.svg"
import { leftBars, resultList, rightBars,easeAnimetion } from "../data";
import { useClientMediaQuery } from "~/hooks/useClientMediaQuery";

export const CauseEffectGraph = () => {
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, {
        once: true,
        margin: "-20% 0px",
    });

    const isMobile = useClientMediaQuery("(max-width: 769px)");

    return (
        <section ref={ref} className="relative grid gap-4 lg:grid-cols-[1fr_360px]">
            <div className="relative rounded-3xl md:bg-softBlue md:pl-[27px] md:pr-[35px] md:pt-4 lg:max-h-[535px]">
                <div className="mx-auto mb-4 md:mb-[40px] w-fit rounded-md bg-secondary300 px-3 pt-[6px] pb-2 text-[14px] font-medium uppercase leading-[20px] tracking-[1.12px] ">
                    When this happens
                </div>

                <div className="grid h-[240px] md:h-[445px] grid-cols-[1fr_auto_1fr]">
                    <div className="relative">
                        <motion.div
                            className="max-w-[520px] text-2xl font-bold leading-tight text-primary500 flex justify-end"
                            initial={{ opacity: 0, y: 12 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{
                                duration: 0.25,
                                delay: 0.2,
                                ease: easeAnimetion,
                            }}
                        >
                            <Paragraph size="xl" className="mx-auto md:max-w-[269px] max-md:px-2 text-[14px] font-normal leading-[18.2px] md:text-[20px] md:font-medium md:leading-[28px] text-gray400">
                                Progesterone drops, estradiol becomes too low or unstable
                            </Paragraph>
                        </motion.div>

                        <div className="absolute bottom-0 left-0 flex w-full h-[123px] md:h-[281px] items-end gap-[1px] md:gap-[2px]">
                            {leftBars.map((bar, index) => (
                                <motion.div
                                    key={index}
                                    className="relative origin-bottom overflow-hidden max-md:rounded-2xl rounded-t-2xl bg-[#D7E1FF]"
                                    style={{ height: `${bar.outer}%` , width: `${100 / leftBars.length}%`}}
                                    initial={{ scaleY: 0 }}
                                    animate={isInView ? { scaleY: 1 } : {}}
                                    transition={{
                                        duration: 0.25,
                                        delay: index * 0.017,
                                        ease: easeAnimetion,
                                    }}
                                >
                                    <motion.div
                                        className="absolute bottom-0 left-0 right-0 origin-bottom rounded-t-2xl bg-primary300/20"
                                        style={{ height: `${bar.inner}%` }}
                                        initial={{ scaleY: 0 }}
                                        animate={isInView ? { scaleY: 1 } : {}}
                                        transition={{
                                            duration: 0.25,
                                            delay: 0.12 + index * 0.017,
                                            ease: easeAnimetion,
                                        }}
                                    />
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <motion.div
                        className="h-[50%] md:h-full max-md:!translate-y-[100%] border-l-[1px] md:border-l-2 border-dashed border-primary500"
                        initial={{ scaleY: 0 }}
                        animate={isInView ? { scaleY: 1 } : {}}
                        transition={{
                            duration: 0.25,
                            delay: 0.4,
                            ease: easeAnimetion,
                        }}
                        style={{ transformOrigin: "top" }}
                    />

                    <div className="relative">
                        <motion.div
                            className="max-w-[520px] text-2xl font-bold leading-tight text-primary500 flex justify-end"
                            initial={{ opacity: 0, y: 12 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{
                                duration: 0.25,
                                delay: 0.5,
                                ease: easeAnimetion,
                            }}
                        >
                            <Paragraph size="xl" className="max-w-[400px] px-2 text-[14px] font-normal leading-[18.2px] md:text-[20px] md:font-medium md:leading-[28px] text-primary500">
                                The brain increases LH (Luteinizing hormone), the ovaries produce more testosterone
                            </Paragraph>
                        </motion.div>

                        <motion.div
                            className="absolute left-4 md:left-12 top-[90px] h-[67px] md:h-[153px] w-[149px] md:w-[342px]"
                            initial={{ clipPath: "inset(0 100% 0 0)" }}
                            animate={isInView ? { clipPath: "inset(0 0% 0 0)" } : {}}
                            transition={{
                                duration: 0.30,
                                delay: 1.94,
                                ease: easeAnimetion,
                            }}
                        >

                            <GraphIcon className="h-full w-full overflow-visible" />   
                        </motion.div>

                        <div className="absolute w-full bottom-0 left-0 flex h-[129px] md:h-[296px] items-end gap-[1px] md:gap-[2px]">
                            {rightBars.map((bar, index) => (
                                <motion.div
                                    key={index}
                                    className="relative origin-bottom overflow-hidden  max-md:rounded-2xl  rounded-t-2xl bg-primary400"
                                    style={{ height: `${bar.outer}%`,width: `${100 / rightBars.length}%` }}
                                    initial={{ scaleY: 0 }}
                                    animate={isInView ? { scaleY: 1 } : {}}
                                    transition={{
                                        duration: 0.25,
                                        delay: 0.57 + index * 0.035,
                                        ease: easeAnimetion,
                                    }}
                                >
                                    <motion.div
                                        className="absolute bottom-0 left-0 right-0 origin-bottom rounded-t-2xl bg-trueBlack/25"
                                        style={{ height: `${bar.inner}%` }}
                                        initial={{ scaleY: 0 }}
                                        animate={isInView ? { scaleY: 1 } : {}}
                                        transition={{
                                            duration: 0.25,
                                            delay: 0.64 + index * 0.035,
                                            ease: easeAnimetion,
                                        }}
                                    />
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>

                <motion.div
                    className="absolute z-20 flex size-12 items-center justify-center rounded-full bg-white text-5xl max-md:bottom-[-60px] max-md:right-[calc(50%-24px)] max-md:translate-x-1/2 max-md:-translate-y-1/2 md:right-[-36px] md:top-[45%]"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={
                        isInView
                            ? isMobile
                                ? {
                                    opacity: 1,
                                    y: [0, 12, 0],
                                    scale: [1, 1.05, 1],
                                }
                                : {
                                    opacity: 1,
                                    x: [0, 10, 0],
                                    scale: [1, 1.05, 1],
                                }
                            : {}
                    }
                    transition={{
                        delay: 1.50,
                        duration: 0.8,
                        ease: easeAnimetion,
                    }}
                >
                    <RightArrowIcon className="scale-150 max-md:rotate-90" />
                </motion.div>
            </div>

            <motion.div
                className="relative flex max-h-[535px] flex-col rounded-3xl md:bg-softBlue md:px-8 max-md:pt-[58px] py-4"
                initial={{ opacity: 0, y: 44 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{
                    duration: 0.30,
                    delay: 0.90,
                    ease: easeAnimetion,
                }}
            >
                <div
                    className="absolute bottom-0 left-[-22px] z-[-2] h-[calc(100%+250px)] w-[100vw] rounded-b-2xl bg-[#FAFBFD] md:hidden"
                    style={{
                        backgroundImage: `
                            linear-gradient(
                                to bottom,
                                #FFFFFF 0%,
                                #FFFFFF 12%,
                                rgba(255, 255, 255, 0.9) 22%,
                                rgba(250, 251, 253, 0.55) 34%,
                                rgba(250, 251, 253, 0) 48%
                            ),
                            linear-gradient(
                                to bottom,
                                #FFFFFF 0%,
                                #FAFBFD 26%,
                                #EAF0FB 62%,
                                #B8C4DC 100%
                            )
                        `,
                        backgroundPosition: "top center, center",
                        backgroundSize: "100% 360px, cover",
                        backgroundRepeat: "no-repeat",
                    }}
                />

                <Reveal
                    className="absolute bottom-0 left-[-22px] z-[-1] h-[calc(100%+250px)] w-[100vw] overflow-hidden rounded-b-2xl md:hidden"
                    delay={1.2}
                >
                    <div
                        className="h-full w-full"
                        style={{
                            backgroundImage: "url('https://res.cloudinary.com/dirscuuhp/image/upload/v1779374063/new_design/Frame_2087329676_1_f35k6f.webp')",
                            backgroundPosition: "right bottom",
                            backgroundSize: "auto 505px",
                            backgroundRepeat: "no-repeat",
                        }}
                    />
                </Reveal>

                <div className="mx-auto mb-5 w-fit shrink-0 rounded-md bg-secondary300 px-3 pt-[6px] pb-2 text-[14px] font-medium uppercase leading-[20px] tracking-[1.12px] md:mb-8">
                    Result
                </div>

                <ul className="flex flex-1 flex-col max-md:mb-4 space-y-2 text-[20px] font-medium text-primary500">
                    {resultList.map((item) => (
                        <li key={item.text} className="flex items-center gap-2">
                            {item.iconElement}

                            <span className="font-suisseBP text-center text-[16px] font-normal leading-[130%] text-primary500 md:text-[20px] md:leading-[140%]">{item.text}</span>
                        </li>
                    ))}
                </ul>

                <motion.div
                    className="shrink-0 max-md:text-center mt-2 rounded-2xl bg-white py-6 px-4 text-[17px] font-medium leading-[23.4px] text-primary500"
                    initial={{ opacity: 0, y: 16 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{
                        duration: 0.25,
                        delay: 1.09,
                        ease: easeAnimetion,
                    }}
                >
                    Weight gain & high testosterone is the result—not the root cause.
                </motion.div>
            </motion.div>
        </section>
    );
};